import React from "react";
import i18n from "i18next";
import { Sparkles, RefreshCw, Home, MessageCircle } from "lucide-react";
import { posthog } from "@/lib/posthog";

interface ErrorBoundaryProps {
  readonly children: React.ReactNode;
  readonly fallbackTitle?: string;
}

interface ErrorBoundaryState {
  readonly hasError: boolean;
  readonly error: Error | null;
}

const SUPPORT_MAILTO =
  "mailto:support@viral.partners?subject=Pomoc%20-%20b%C5%82%C4%85d%20w%20aplikacji";

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    try {
      posthog.captureException(error, {
        source: "react_error_boundary",
        component_stack: info.componentStack ?? null,
      });
    } catch {
      // posthog not initialized — swallow
    }
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  private handleGoHome = (): void => {
    window.location.href = "/";
  };

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;

    const isDev = import.meta.env.DEV;
    const techMessage = this.state.error?.message ?? i18n.t("errorBoundary.unknownError");
    const stack = this.state.error?.stack ?? "";

    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="w-full max-w-xl rounded-2xl border border-white/[0.08] bg-gradient-to-b from-white/[0.04] to-transparent p-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-400/10 border border-amber-400/20 mb-5">
            <Sparkles className="w-5 h-5 text-amber-300" />
          </div>
          <h2 className="text-xl font-semibold text-white/90 mb-3">
            {this.props.fallbackTitle ?? i18n.t("errorBoundary.defaultTitle")}
          </h2>
          <p className="text-sm text-white/60 leading-relaxed mb-6">
            {i18n.t("errorBoundary.description")}
          </p>

          <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
            <button
              type="button"
              onClick={this.handleReset}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-black text-sm font-medium hover:bg-white/90 transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              {i18n.t("errorBoundary.retry")}
            </button>
            <button
              type="button"
              onClick={this.handleGoHome}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.1] text-sm text-white/80 transition-colors"
            >
              <Home className="w-3.5 h-3.5" />
              {i18n.t("errorBoundary.goHome")}
            </button>
            <a
              href={SUPPORT_MAILTO}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.1] text-sm text-white/80 transition-colors"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              {i18n.t("errorBoundary.contactSupport")}
            </a>
          </div>

          {isDev ? (
            <details className="text-left rounded-lg bg-black/40 border border-white/[0.08] p-3">
              <summary className="text-[11px] text-white/40 cursor-pointer select-none">
                {i18n.t("errorBoundary.techDetails")}
              </summary>
              <p className="text-xs font-mono text-red-300/90 break-all whitespace-pre-wrap mt-2">
                {techMessage}
              </p>
              {stack ? (
                <pre className="text-[10px] text-white/50 mt-2 overflow-auto max-h-64">{stack}</pre>
              ) : null}
            </details>
          ) : null}
        </div>
      </div>
    );
  }
}
